import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCircleIcon, MailIcon, PhoneIcon, MapPinIcon, GlobeIcon, UsersIcon, DollarSignIcon, BriefcaseIcon, LinkedinIcon, CalendarIcon, CpuIcon, ShareIcon, TagIcon, ClipboardListIcon, MapIcon, ClockIcon, StarIcon, ReceiptIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
const coreFields = [{
  name: 'Owner / partner name',
  description: 'First and last name of managing partners and owners',
  icon: UserCircleIcon
}, {
  name: 'Verified email address',
  description: 'Direct B2B email addresses verified in real-time',
  icon: MailIcon
}, {
  name: 'Direct phone number',
  description: 'Office and direct dial numbers for decision-makers',
  icon: PhoneIcon
}, {
  name: 'Job title',
  description: 'Exact titles like CPA, Managing Partner, Tax Director',
  icon: BriefcaseIcon
}, {
  name: 'Full business address',
  description: 'Complete street address, city, state, and ZIP code',
  icon: MapPinIcon
}, {
  name: 'Company website',
  description: 'Domain names and online presence information',
  icon: GlobeIcon
}, {
  name: 'Employee count',
  description: 'Number of employees to filter by firm size',
  icon: UsersIcon
}, {
  name: 'Estimated revenue',
  description: 'Annual revenue ranges for targeting high-value firms',
  icon: DollarSignIcon
}];
const additionalFields = [{
  name: 'Specialization',
  description: 'CPA, tax prep, bookkeeping, audit, forensic accounting',
  icon: ClipboardListIcon
}, {
  name: 'LinkedIn profiles',
  description: 'Professional LinkedIn URLs for owners and key staff',
  icon: LinkedinIcon
}, {
  name: 'Years in business',
  description: 'Founding year and business longevity data',
  icon: CalendarIcon
}, {
  name: 'Technology stack',
  description: 'Software used (QuickBooks, Xero, Sage, etc.)',
  icon: CpuIcon
}, {
  name: 'Social media profiles',
  description: 'Facebook, Twitter, Instagram business accounts',
  icon: ShareIcon
}, {
  name: 'Number of locations',
  description: 'Total branch and office count across regions',
  icon: MapIcon
}, {
  name: 'Business hours',
  description: 'Operating hours and availability information',
  icon: ClockIcon
}, {
  name: 'Reviews & ratings',
  description: 'Google, Yelp, and industry review scores',
  icon: StarIcon
}, {
  name: 'Industry focus',
  description: 'Verticals served (healthcare, real estate, tech, etc.)',
  icon: TagIcon
}, {
  name: 'Avg. ticket size',
  description: 'Average transaction or engagement value per client',
  icon: ReceiptIcon
}];
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0
  }
};
export function AccountingFields() {
  const [showAll, setShowAll] = useState(false);
  return <section className="py-24 bg-slate-50" data-id="element-364">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-365">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-366">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-367">
            What's included in the accounting database?
          </h2>
          <p className="text-xl text-slate-600" data-id="element-368">
            Our accounting owner contacts are enriched with{' '}
            <strong className="text-blue-600" data-id="element-369">30+ data points</strong> to help
            you segment, personalize, and convert your ideal prospects.
          </p>
        </div>

        {/* Core Fields - Always Visible */}
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-370">
          {coreFields.map(field => <motion.div key={field.name} variants={item} whileHover={{
          y: -4,
          transition: {
            duration: 0.2
          }
        }} className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group" data-id="element-371">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-372">
                <field.icon className="w-6 h-6" data-id="element-373" />
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-374">
                {field.name}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-375">
                {field.description}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Additional Fields - Expandable */}
        <AnimatePresence data-id="element-376">
          {showAll && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-377">
              <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-378">
                {additionalFields.map(field => <motion.div key={field.name} variants={item} whileHover={{
              y: -4,
              transition: {
                duration: 0.2
              }
            }} className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group" data-id="element-379">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-380">
                      <field.icon className="w-6 h-6" data-id="element-381" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-382">
                      {field.name}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed" data-id="element-383">
                      {field.description}
                    </p>
                  </motion.div>)}
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Expand/Collapse Button */}
        <div className="text-center" data-id="element-384">
          <button onClick={() => setShowAll(!showAll)} className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 font-semibold rounded-full hover:bg-blue-600 hover:text-white transition-all" data-id="element-385">
            {showAll ? <>
                Show less
                <ChevronUpIcon className="w-5 h-5" data-id="element-386" />
              </> : <>
                View all 30+ data points
                <ChevronDownIcon className="w-5 h-5" data-id="element-387" />
              </>}
          </button>
        </div>
      </div>
    </section>;
}